/**
 * Health Integration Types
 *
 * These types support bidirectional health sync:
 * - EXPORT: App data → Apple Health / Health Connect
 * - IMPORT: Apple Health / Health Connect → App data
 *
 * @see /docs/APPLE_HEALTH_INTEGRATION_PLAN.md for full implementation details
 */

/**
 * Provider ID constants - MUST match original Marune app exactly
 */
export const HEALTH_PROVIDER_IDS = {
  IOS_HEALTHKIT: 'ios-healthKit',
  ANDROID_GOOGLE_FIT: 'android-googleFit',
  ANDROID_HEALTH_CONNECT: 'android-healthConnect',
} as const;

export type HealthProviderId = typeof HEALTH_PROVIDER_IDS[keyof typeof HEALTH_PROVIDER_IDS];

/**
 * Health sync metadata - matches original Marune app exactly
 * Used for EXPORT (app → Apple Health / Google Fit)
 *
 * This is stored on Training, Exercise, and WorkoutSession entities
 * after they are successfully synced to a health provider.
 */
export interface HealthProvidersMetadata {
  sync: {
    'ios-healthKit'?: boolean;
    'android-googleFit'?: boolean;
  };
}

/**
 * Import metadata - NEW Phoenix feature
 * Used for IMPORT (Apple Health / Health Connect → app)
 *
 * Original Marune app will ignore this field (safe to add).
 * Stored on Training and Exercise entities that were imported from health providers.
 */
export interface ImportedFromMetadata {
  /** Which health provider the data came from */
  provider: 'ios-healthKit' | 'android-healthConnect';

  /** Human-readable source app name (e.g., "Whoop", "Apple Watch", "Peloton") */
  sourceApp: string;

  /** App bundle identifier (e.g., "com.whoop.whoop") */
  sourceBundleId: string;

  /** HealthKit/Health Connect UUID - CRITICAL for duplicate prevention */
  originalUuid: string;

  /** Timestamp when this workout was imported into Marune */
  importedAt: number;

  /** Optional: preserve original values that don't map to Marune fields */
  originalData?: {
    calories?: number;
    distance?: number;  // in meters
    heartRateSamples?: number;
  };
}

/**
 * HealthKit workout data returned from native plugin query
 */
export interface HealthKitWorkout {
  /** Unique identifier in HealthKit - used for duplicate detection */
  uuid: string;

  /** ISO date string */
  startDate: string;

  /** ISO date string */
  endDate: string;

  /** Duration in seconds */
  duration: number;

  /** HealthKit activity type ID (e.g., 16 = Boxing) */
  workoutActivityId: number;

  /** Human-readable activity name (e.g., "Boxing") */
  workoutActivityName: string;

  /** Total calories burned (kcal) */
  totalEnergyBurned: number;

  /** Total distance in meters */
  totalDistance: number;

  /** Source app name (e.g., "Whoop", "Apple Watch") */
  source: string;

  /** Source app bundle identifier */
  sourceBundleId: string;

  /** Device information if available */
  device: {
    name: string;
    manufacturer: string;
    model: string;
  } | null;

  /**
   * IANA timezone name (e.g. "America/New_York") at the time the workout was
   * recorded, when the source app set HKMetadataKeyTimeZone. Populated by the
   * native plugin reading workout.metadata[HKMetadataKeyTimeZone]. May be
   * absent: not every HealthKit source writes this key, and our current
   * Swift plugin (HeartRateWorkoutPlugin) does not yet forward it — see
   * Phoenix/ios/App/App/HeartRateWorkoutPlugin.swift:510-533.
   *
   * When present, the import path uses it to anchor the training to the
   * workout's original calendar day; otherwise it falls back to the
   * importing device's current offset.
   */
  timeZone?: string;
}

/**
 * Firebase index entry for tracking imported workouts
 * Path: /users/{userId}/importedHealthWorkouts/{healthKitUuid}
 *
 * Used for O(1) duplicate detection before importing
 */
export interface ImportedWorkoutIndex {
  /** Type of entity created from the import */
  entityType: 'training' | 'exercise';

  /** Firebase key of the created Training or Exercise */
  entityId: string;

  /** Timestamp of import */
  importedAt: number;

  /** Source app for reference */
  sourceApp: string;
}

/**
 * User's health sync preferences
 * Stored locally in AsyncStorage/Preferences
 */
export interface HealthSyncSettings {
  /** Enabled providers by ID */
  enabledProviders: Record<HealthProviderId, boolean>;

  /** Auto-sync trainings to health when saved */
  autoSyncTrainings?: boolean;

  /** Auto-sync exercises to health when saved */
  autoSyncExercises?: boolean;

  /** Auto-sync workout sessions to health when saved */
  autoSyncWorkouts?: boolean;

  /** Sync weight bidirectionally */
  syncWeight?: boolean;
}
